"use client";

import { useEffect, useState, useRef } from "react";
import Image, { getImageProps } from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
// import "swiper/css";
// import "swiper/css/navigation";
import VideoModal from "@/components/videoModel";


// Function to extract YouTube ID from URL
const getYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m?.[1] ?? null;
};

function StudentStoriesSlider({ title, subText, students }) {
    const [modal, setModal] = useState({ open: false, url: "" });
    const videoRefs = useRef({});
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Check if the user is on a mobile device (touch-enabled or small screen)
        const checkMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth < 1024; // lg breakpoint
            setIsMobile(isTouchDevice || isSmallScreen);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Only handle video playback on desktop
        if (isMobile) return;

        // Reset videos if hoveredIndex changes
        Object.values(videoRefs.current).forEach((video) => {
            if (!video) return;
            video.pause();
            video.currentTime = 0;
        });

        if (hoveredIndex !== null && videoRefs.current[hoveredIndex]) {
            const video = videoRefs.current[hoveredIndex];
            video.currentTime = 0;
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => { });
        }
    }, [hoveredIndex, isMobile]);

    const handleCardInteraction = (index) => {
        // Only enable hover on desktop
        if (!isMobile) {
            setHoveredIndex(index);
        }
    };

    return (
        <section className="w-full">
            <VideoModal
                open={modal.open}
                url={modal.url}
                onClose={() => setModal({ open: false, url: "" })}
            />

            <div className="w-full flex items-start justify-between gap-4">
                <div className="w-full flex flex-row items-start justify-between">
                    <div className={`w-full text-black space-y-2 mb-8 max-w-[700px]`}>
                        <h2 className="text-2xl sm:text-3xl lg:text-[42px] font-saansBold">{title}</h2>
                        {subText && <p className="mb-8 text-base sm:text-lg lg:text-[18px] max-w-5xl">{subText}</p>}
                    </div>
                </div>
            </div>
            <div className="relative w-full">
                <Swiper
                    modules={[Navigation, Autoplay]}
                    loop
                    autoplay={{ delay: 30000, disableOnInteraction: false }}
                    onSlideChange={() => setHoveredIndex(null)}
                    spaceBetween={5}
                    slidesPerView={1.3}
                    breakpoints={{
                        490: { slidesPerView: 1.5 },
                        640: { slidesPerView: 2.1 },
                        768: { slidesPerView: 2.1 },
                        992: { slidesPerView: 2.1 },
                        1024: { slidesPerView: 2.5 },
                        1280: { slidesPerView: 3.1 },
                    }}
                    navigation={{
                        prevEl: ".stories-prev",
                        nextEl: ".stories-next",
                    }}
                    className="w-full"
                >
                    {students.map((s, index) => {

                          const rawPosterSrc = getYouTubeId(s.videLink)
                                ? `https://img.youtube.com/vi/${getYouTubeId(s.videLink)}/hqdefault.jpg`
                                : s.image?.src || s.image || s.thumbnail || "";

                            const { props: { src: optimizedPosterSrc } } = rawPosterSrc
                                ? getImageProps({
                                    src: rawPosterSrc,
                                    alt: "",
                                    width: 435,
                                    height: 775,
                                    quality: 75,
                                })
                                : { props: { src: "" } };
                        return (
                        <SwiperSlide key={index} className="flex flex-col items-center">
                            <div
                                className="story_media w-full max-w-[435px] aspect-[435/775] mx-auto shadow-lg group overflow-hidden"
                                onMouseEnter={() => handleCardInteraction(index)}
                                onMouseLeave={() => !isMobile && setHoveredIndex(null)}
                            >
                                {/* Show video on desktop, image on mobile */}
                                {!isMobile && s.shortUrl ? (
                                    <video
                                        ref={(el) => {
                                            if (el) videoRefs.current[index] = el;
                                            else delete videoRefs.current[index];
                                        }}
                                        src={s.shortUrl}
                                        poster={optimizedPosterSrc}
                                        muted
                                        playsInline
                                        preload="none"
                                        className="object-cover w-full h-full"
                                    />
                                ) : (
                                     <Image
                                        src={rawPosterSrc}
                                        alt={s.name}
                                        width={435}
                                        height={775}
                                        className="object-cover w-full h-full"
                                        loading="lazy"
                                    />
                                )}

                                <div
                                    className="
                                        absolute inset-0 flex flex-col justify-end px-4 py-4 text-left
                                        before:absolute before:inset-0
                                        before:opacity-0
                                        group-hover:before:opacity-100
                                        before:bg-[linear-gradient(180deg,rgba(8,30,45,0)_45.54%,rgba(8,30,45,0.8)_91.22%)]
                                        before:transition-opacity before:duration-500
                                    "
                                >
                                    <div className="relative w-full h-full p-4 border border-white/40 lg:border-transparent lg:group-hover:border-[#FFFFFF66] transition-all duration-300">
                                        <div className="absolute bottom-6 sm:bottom-7 md:bottom-10 lg:bottom-0 inset-0 flex flex-col items-center justify-end p-4 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 translate-y-10 lg:group-hover:translate-y-0 transition-all duration-500 ease-out">
                                            <button
                                                type="button"
                                                onClick={() => setModal({ open: true, url: s.videLink })}
                                                className="cursor-pointer w-12 h-12 rounded-full flex items-center justify-center mb-2 md:mb-0 bg-white text-black shadow-lg hover:scale-105 transition"
                                            >
                                                <Image
                                                    src="/play.svg"
                                                    alt="Play"
                                                    width={15}
                                                    height={14}
                                                    className="ml-1.5"
                                                />
                                            </button>
                                            <p className="relative font-bold text-white text-lg py-3 leading-5">{s.name}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                        )
                    })}
                </Swiper>

                <div className="absolute top-1/2 left-0 right-0 flex justify-between items-center p-2 w-full ">
                    <div className="flex justify-between gap-4 w-full">
                        <button
                            className="stories-prev cursor-pointer bg-[#efefef76] border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition z-1"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-4 h-4 text-gray-700" />
                        </button>
                        <button
                            className="stories-next cursor-pointer bg-[#efefef76]  border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition z-1"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-4 h-4 text-gray-700" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default StudentStoriesSlider;